import React, { useState } from "react";
import { useNavigate, useOutletContext, Link } from "react-router-dom";
import { ChevronLeft, Minus, Plus, Trash2, ShoppingCart, ShoppingBag, Store, StickyNote } from "lucide-react";
import { useStore } from "@/lib/store";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/lib/auth";
import { fmtIDR, productImg, effectivePrice, LOGO } from "@/lib/ellaApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function CartPage() {
  const nav = useNavigate();
  const ctx = useOutletContext();
  const { cartItems, cartByBranch, cartSubtotal, updateCartQty } = useStore();

  useSeo({ title: "Keranjang" });
  const { isAuthed } = useAuth();
  const [busy, setBusy] = useState(false);

  const handleChange = async (item, qty) => {
    setBusy(true);
    const r = await updateCartQty(item.ProductID, qty, item.Notes || "");
    if (r?.message) toast.warning(r.message); // e.g. "Stock hanya tersedia N"
    setBusy(false);
  };

  const handleCheckout = () => {
    if (!cartItems.length) { toast.error("Keranjang masih kosong"); return; }
    if (!isAuthed) {
      toast.info("Masuk dulu untuk melanjutkan checkout");
      ctx?.openAuth?.();
      return;
    }
    nav("/checkout");
  };

  return (
    <div className="pb-32">
      <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-display text-lg font-bold text-gray-900">Keranjang</h1>
        <span className="text-xs text-gray-500 ml-auto" data-testid="cart-item-count">{cartItems.length} item</span>
      </div>

      {cartItems.length === 0 ? (
        <div className="p-8 text-center mt-12" data-testid="empty-cart">
          <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold mb-1">Keranjangmu kosong</h2>
          <p className="text-sm text-gray-500 mb-5">Yuk pilih produk frozen favoritmu!</p>
          <Link to="/" className="inline-flex items-center gap-2 btn-primary px-5 py-2.5" data-testid="empty-cart-shop-button">
            <ShoppingBag className="w-4 h-4" /> Mulai Belanja
          </Link>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {/* Items grouped per branch — each branch ships (and bills ongkir) separately */}
          {cartByBranch.map((group) => (
          <section key={group.branch} data-testid={`cart-branch-${group.branch}`}>
            <div className="flex items-center justify-between mb-2 px-0.5">
              <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                <Store className="w-3.5 h-3.5 text-[#D32F2F]" /> Cabang {group.branch}
                <span className="font-normal text-gray-400">• {group.items.length} produk</span>
              </span>
              <span className="text-xs font-semibold text-gray-500" data-testid={`cart-branch-subtotal-${group.branch}`}>{fmtIDR(group.subtotal)}</span>
            </div>
            <div className="space-y-3">
          {group.items.map((item) => {
            const finalPrice = effectivePrice(item.Price, item.DiscountType, item.Discount);
            const qty = Number(item.Qty || 0);

            return (
              <div key={item.ProductID} className="bg-white rounded-2xl border border-gray-100 p-3 flex gap-3" data-testid={`cart-item-${item.ProductID}`}>
                <Link to={`/product/${item.ProductID}`} className="shrink-0" data-testid={`cart-item-image-link-${item.ProductID}`}>
                  <img
                    src={productImg(item.ImagePath)}
                    alt={item.Product}
                    className="w-16 h-16 rounded-xl object-cover bg-gray-50"
                    onError={(e)=>{ e.target.src=LOGO; e.target.classList.add("object-contain","p-2","opacity-40"); }}
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.ProductID}`} data-testid={`cart-item-title-link-${item.ProductID}`}>
                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug hover:text-[#D32F2F] transition-colors">{item.Product}</h3>
                  </Link>
                  <p className="text-sm font-bold text-[#D32F2F] mt-0.5" data-testid={`cart-item-price-${item.ProductID}`}>{fmtIDR(finalPrice)}</p>
                  {item.Notes && (
                    <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1 min-w-0">
                      <StickyNote className="w-3 h-3 shrink-0" />
                      <span className="line-clamp-1">{item.Notes}</span>
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleChange(item, 0)}
                      disabled={busy}
                      className="text-gray-400 hover:text-[#D32F2F] p-1 -ml-1"
                      data-testid={`cart-item-remove-${item.ProductID}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-2 bg-gray-50 rounded-full p-1">
                      <button onClick={() => handleChange(item, Math.max(0, qty - 1))} disabled={busy} className="w-7 h-7 rounded-full bg-white text-gray-700 flex items-center justify-center" data-testid={`cart-decrease-${item.ProductID}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="min-w-[24px] text-center text-sm font-bold" data-testid={`cart-qty-${item.ProductID}`}>{qty}</span>
                      <button onClick={() => handleChange(item, qty + 1)} disabled={busy} className="w-7 h-7 rounded-full bg-[#D32F2F] text-white flex items-center justify-center" data-testid={`cart-increase-${item.ProductID}`}>
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
            </div>
          </section>
          ))}

          <div className="bg-white rounded-2xl border border-gray-100 p-4 mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold" data-testid="cart-subtotal">{fmtIDR(cartSubtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Ongkos kirim</span>
              <span className="text-xs text-gray-400">Dihitung di checkout</span>
            </div>
            <div className="border-t border-gray-100 pt-2 flex justify-between">
              <span className="font-display font-bold">Total</span>
              <span className="font-display font-bold text-lg text-[#D32F2F]">{fmtIDR(cartSubtotal)}</span>
            </div>
          </div>
        </div>
      )}

      {cartItems.length > 0 && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-4 py-3 z-40">
          <Button onClick={handleCheckout} className="w-full h-12 btn-primary" data-testid="cart-checkout-button">
            Lanjut ke Checkout • {fmtIDR(cartSubtotal)}
          </Button>
          {!isAuthed && (
            <p className="text-[11px] text-center text-gray-500 mt-1.5">Login dulu untuk checkout • <button onClick={() => ctx?.openAuth?.()} className="text-[#D32F2F] font-semibold underline">Masuk / Daftar</button></p>
          )}
        </div>
      )}
    </div>
  );
}
